"use client";
import { Check, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

interface PlanProps {
  title: string;
  popular: boolean;
  price: number;
  buttonLabel?: string;
  features: string[];
  priceId: string;
  anchorPrice: number;
}

const plans: PlanProps[] = [
  {
    title: "Starter",
    popular: false,
    price: 100,
    anchorPrice: 200,
    priceId: "",
    buttonLabel: "Purchase Now",
    features: [
      "NextJS boilerplate",
      "Blog & Doc",
      "Sendgrid / Resend email",
      "Stripe / Lemon Sqeezy",
      "Social Login / Magic Link",
      "Open Ai",
      "Lifetime updates",
    ],
  },
  {
    title: "Ulta Pro",
    popular: true,
    price: 200,
    anchorPrice: 300,
    priceId: "",
    buttonLabel: "Purchase Now",
    features: [
      "NextJS boilerplate",
      "Blog & Doc",
      "Sendgrid / Resend email",
      "Stripe / Lemon Sqeezy",
      "Social Login / Magic Link",
      "Open Ai",
      "S3 / Cloudinary / Supabase storage",
      "ChatGPT prompts for terms & privacy",
      "Other Ui tailwind library links",
      "Lifetime updates",
    ],
  },
];

export default function Pricing02() {
  return (
    <section className="pb-20 pt-20 md:pb-32 md:pt-32 container mx-auto">
      <div className="text-center space-y-4 pb-16 mx-auto max-w-4xl">
        <Badge>PRICING</Badge>
        <h2 className="mx-auto mt-4 text-3xl font-bold sm:text-5xl tracking-tight">
          Pricing
        </h2>
        <p className="text-xl text-muted-foreground pt-1">
          Don&apos;t just take our word for it. Here&apos;s what others have to
          say.
        </p>
      </div>
      <section className="flex flex-col sm:flex-row sm:flex-wrap justify-center gap-8">
        {plans.map((plan: PlanProps, index) => (
          <PlanCard key={index} plan={plan} />
        ))}
      </section>
    </section>
  );
}

function PlanCard({ plan }: { plan: PlanProps }) {
  return (
    <div
      className={`relative bg-card rounded-xl text-card-foreground shadow w-full sm:w-96 justify-between py-1 mx-auto sm:mx-0 flex flex-col ${
        plan.popular ? "border-4 border-primary/50" : "border"
      }`}
    >
      <div className="flex flex-col space-y-1.5 p-6">
        <div className="flex justify-between">
          <h3 className="text-lg lg:text-xl font-bold ">{plan.title}</h3>
          {plan.popular && (
            <div
              className="font-medium whitespace-nowrap rounded-lg px-2.5 h-fit text-sm py-1 bg-primary text-primary-foreground"
              style={{
                position: "absolute",
                top: "-15px",
                right: "23px",
              }}
            >
              Popular
            </div>
          )}
        </div>
        <div>
          <div className="flex gap-0.5 pt-6">
            <div className="flex gap-2">
              <div className="flex flex-col justify-end mb-[4px] text-lg ">
                <p className="relative opacity-80">
                  <span className="absolute bg-base-content h-[1.5px] inset-x-0 top-[48%]"></span>
                  <span className="text-base-content line-through">
                    {plan.anchorPrice}
                  </span>
                </p>
              </div>
              <p className="text-5xl tracking-tight font-extrabold">
                {plan.price}
              </p>
              <div className="flex flex-col justify-end mb-[4px]">
                <p className="text-xs opacity-60 uppercase font-semibold">
                  USD
                </p>
              </div>
            </div>
          </div>
        </div>
        <div className="pt-6 flex flex-col gap-2">
          {plan.features.map((feature: string, index: number) => (
            <Feature key={index} text={feature} />
          ))}
        </div>
      </div>
      <div className="flex flex-col mt-auto items-center pt-0 p-6 w-full">
        <Button variant="default" className="w-full !py-6">
          Get Started{" "}
          <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 text-white" />
        </Button>
        <p className="flex mt-2 text-muted-foreground items-center justify-center gap-2 text-sm text-center font-medium">
          Pay once. Build unlimited projects!
        </p>
      </div>
    </div>
  );
}

function Feature({ text }: { text: string }) {
  return (
    <div className="flex gap-2 items-center space-y-1 leading-relaxed text-base">
      <Check className="mt-1" size={18} />
      <p className="font-medium">{text}</p>
    </div>
  );
}
